import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Sparkles } from '@videoblocks/react-icons';

import { selectUserSubscriptionUniqueId } from '../../../../../auth/AuthSelectors';
import { ContentEditSegment } from '../../../../../auth/AuthTypes';
import { Button } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Button/Button';
import {
  selectContentEditSegment,
  selectCreditBalance,
  selectVideoEditorModalStockItem,
} from '../../../../ContentEdit/ContentEditSlice';
import { useVideoUI, useVideoSelection } from './context/VideoEditorContext';
import { handlePurchaseCredits } from './utils/purchaseUtils';

const useGenerateButtonLogic = (onGenerateVideo: () => void) => {
  const contentEditSegment = useSelector(
    selectContentEditSegment
  ) as ContentEditSegment;
  const creditBalance = useSelector(selectCreditBalance);
  const userSubscriptionUniqueId = useSelector(selectUserSubscriptionUniqueId);
  const selectedStockItem = useSelector(selectVideoEditorModalStockItem);
  const { promptInputRef, selectedUseCase, defaultPlaceholder } = useVideoUI();
  const { selection } = useVideoSelection();

  const buttonConfig = useMemo(() => {
    const config: Record<
      ContentEditSegment,
      { action: () => void; text: string }
    > = {
      unsubscribed: {
        action: () => {
          Telemetry.increment('ai.contentEdit.buttonClick.pricingRedirect');
          window.location.href = '/pricing';
        },
        text: 'Subscribe to Generate',
      },
      subscribed_user: {
        action: () => {
          onGenerateVideo();
        },
        text: 'Generate Video',
      },
      upgradable_user: {
        action: () => {
          handlePurchaseCredits({
            selectedStockItem,
            selection,
            selectedUseCase,
            defaultPlaceholder,
            promptText: promptInputRef.current?.getPromptValue() || '',
            planId: userSubscriptionUniqueId,
          }); // Redirect to upgrade
        },
        text: 'Upgrade to Generate',
      },
      disabled: {
        action: () => {},
        text: 'Disabled',
      },
    };

    return config[contentEditSegment];
  }, [
    contentEditSegment,
    creditBalance,
    onGenerateVideo,
    handlePurchaseCredits,
  ]);

  return { buttonConfig };
};

export default function GenerateButton({ disabled, onGenerateVideo }) {
  const { buttonConfig } = useGenerateButtonLogic(onGenerateVideo);

  return (
    <Button disabled={disabled} onClick={buttonConfig.action}>
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 fill-black" />
        {buttonConfig.text}
      </div>
    </Button>
  );
}
